// dateUtil.js

/**
 * Utility function to format a date string into various formats.
 *
 * @param {string} dateStr - The date string in the format 'YYYY-MM-DD HH:mm:ss'.
 * @param {string} format - The desired output format. Options:
 *                          'shortDate', 'longDate', 'time', 'dateTime', 'iso', 'custom', 'MM/DD/YY'
 * @param {string} [customFormat] - Custom format, using 'DD', 'MM', 'YYYY', 'HH', 'mm', 'ss' as tokens.
 * @returns {string} - The formatted date.
 */
export function formatDate(dateStr, format, customFormat) {
    const date = new Date(dateStr.replace(' ', 'T')); // Convert to ISO format if necessary

    switch (format) {
        case 'shortDate': // Example: 09/05/2024
            return date.toLocaleDateString('en-US');
        case 'longDate': // Example: September 5, 2024
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        case 'time': // Example: 12:58:06 PM
            return date.toLocaleTimeString('en-US');
        case 'dateTime': // Example: September 5, 2024, 12:58:06 PM
            return date.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' });
        case 'iso': // Example: 2024-09-05T12:58:06.000Z (ISO 8601 format)
            return date.toISOString();
        case 'MM/DD/YY': // Example: 09/05/24
            return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
        case 'custom': // Custom format based on user-defined tokens
            return customDateFormat(date, customFormat);
        default:
            throw new Error("Invalid format type. You must input a format type such as 'MM/DD/YY' or 'shortDate");
    }
}

/**
 * Custom date format function.
 *
 * @param {Date} date - The date object.
 * @param {string} format - Custom format using tokens 'DD', 'MM', 'YYYY', 'HH', 'mm', 'ss'.
 * @returns {string} - The formatted date string.
 */
function customDateFormat(date, format) {
    const tokens = {
        DD: String(date.getDate()).padStart(2, '0'),
        MM: String(date.getMonth() + 1).padStart(2, '0'), // Months are 0-based
        YYYY: date.getFullYear(),
        YY: String(date.getFullYear()).slice(-2), // Two-digit year
        HH: String(date.getHours()).padStart(2, '0'),
        mm: String(date.getMinutes()).padStart(2, '0'),
        ss: String(date.getSeconds()).padStart(2, '0')
    };

    return format.replace(/DD|MM|YYYY|YY|HH|mm|ss/g, (token) => tokens[token] || token);
}

